import React, { FC } from "react";
import { Box } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import { CategoryData } from "../types/dashboardTypes";
import { translateDashboardDistributionLabel } from "../utils/dashboardI18n";

interface ProductItemsByStateBarChartProps {
  data: CategoryData[];
}

/** Matches labels from stringFormatter(ProductItemState). */
const BAR_COLORS: Record<string, string> = {
  Created: "#1565c0",
  "In Stock": "#2e7d32",
  Reserved: "#f9a825",
  Sold: "#e65100",
  Damaged: "#c62828",
  Expired: "#6a1b9a",
  Lost: "#5d4037",
  Consumed: "#00838f",
  Deleted: "#9e9e9e"
};

const FALLBACK_PALETTE = [
  "#1976d2",
  "#2e7d32",
  "#f9a825",
  "#7b1fa2",
  "#00838f",
  "#6d4c41",
  "#e65100",
  "#5d4037",
  "#455a64"
];

const colorsForLabels = (labels: string[]): string[] =>
  labels.map(
    (label, i) =>
      BAR_COLORS[label] ?? FALLBACK_PALETTE[i % FALLBACK_PALETTE.length]
  );

/**
 * Vertical bars: states on the x-axis (horizontal), counts on the y-axis.
 */
const ProductItemsByStateBarChart: FC<ProductItemsByStateBarChartProps> = ({
  data
}) => {
  const { t } = useKivunovaTranslation();
  const labels = data.map((d) => d.label);
  const values = data.map((d) => d.value);
  const colors = colorsForLabels(labels);

  if (data.length === 0) {
    return null;
  }

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <BarChart
        height={360}
        xAxis={[
          {
            scaleType: "band",
            data: labels,
            valueFormatter: (v) =>
              translateDashboardDistributionLabel(String(v), t),
            colorMap: {
              type: "ordinal",
              values: labels,
              colors
            },
            tickLabelStyle: {
              angle: -42,
              textAnchor: "end",
              fontSize: 11
            }
          }
        ]}
        yAxis={[{ scaleType: "linear", tickMinStep: 1 }]}
        series={[
          {
            data: values,
            label: t("dashboard-product-items-series-label", "Items")
          }
        ]}
        margin={{ top: 24, right: 24, bottom: 88, left: 48 }}
        hideLegend
      />
    </Box>
  );
};

export default ProductItemsByStateBarChart;
